/**
 * Centralized Icon Library - "Icon Palette"
 * 
 * This file serves as the single source of truth for all icons used in the application.
 * Similar to a color palette, this provides an easy overview and centralized management.
 * 
 * Icons are sourced from Iconify, which provides access to 200,000+ icons through
 * on-demand loading (1-2KB per icon).
 * 
 * Usage:
 * ```tsx
 * import { Icon, ICONS } from '@/lib/icons'
 * 
 * <Icon icon={ICONS.user} width={20} height={20} />
 * ```
 */

import { Icon as IconifyIcon } from '@iconify/react'

// Re-export Icon component for convenience
export { Icon } from '@iconify/react'

/**
 * Sport Icons - Racket/Paddle sports with proper equipment representation
 * Using Material Design Icons (mdi:) and Game Icons (game-icons:) for authentic visuals
 */
export const SPORT_ICONS = {
  // Racket sports with proper equipment icons
  tableTennis: 'mdi:table-tennis',        // Ping pong paddle
  tennis: 'mdi:tennis',                    // Tennis racket
  badminton: 'mdi:badminton',              // Badminton racket
  squash: 'mdi:squash',                    // Squash racquet with ball
  padel: 'mdi:tennis',                     // Padel racket (similar to tennis)
  pickleball: 'mdi:table-tennis',          // Pickleball paddle (similar to table tennis)
  racquetball: 'mdi:racquetball',          // Racquetball racket
  beachTennis: 'mdi:tennis',               // Beach tennis racket
  chess: 'mdi:chess-knight',               // Chess knight piece
  tableHockey: 'mdi:hockey-sticks',        // Table hockey sticks
  foosball: 'mdi:soccer',                  // Foosball (table football)
} as const

/**
 * Competition Icons - Tournament and ranking related
 */
export const COMPETITION_ICONS = {
  trophy: 'mdi:trophy',                    // Klubbspel brand icon / Tournament winner
  medal: 'mdi:medal',                      // Rankings/achievements
  chart: 'mdi:chart-line',                 // Statistics/leaderboard
  podium: 'mdi:podium',                    // Rankings/leaderboard (winner podium)
  cup: 'mdi:cup',                          // Legacy - kept for compatibility
  tournament: 'mdi:tournament',            // Series/tournament (bracket structure)
} as const

/**
 * User Icons - People and profile related
 */
export const USER_ICONS = {
  user: 'mdi:account',                     // Single user
  users: 'mdi:account-group',              // Multiple users
  userPlus: 'mdi:account-plus',            // Add user
  userMinus: 'mdi:account-minus',          // Remove user
  userX: 'mdi:account-remove',             // Remove/delete user
  userCheck: 'mdi:account-check',          // Verified user
  userCircle: 'mdi:account-circle',        // User avatar
  shield: 'mdi:shield-account',            // Admin/security
  mail: 'mdi:email',                       // Email
  login: 'mdi:login',                      // Login action
  logout: 'mdi:logout',                    // Logout action
} as const

/**
 * Organization Icons - Clubs and buildings
 */
export const ORGANIZATION_ICONS = {
  buildings: 'mdi:office-building',        // Organization/club
  club: 'mdi:home-group',                  // Club/community
} as const

/**
 * Action Icons - Common user actions
 */
export const ACTION_ICONS = {
  add: 'mdi:plus',                         // Add/create
  edit: 'mdi:pencil',                      // Edit
  delete: 'mdi:delete',                    // Delete/trash
  export: 'mdi:export',                    // Export data
  search: 'mdi:magnify',                   // Search
  save: 'mdi:content-save',                // Save
  close: 'mdi:close',                      // Close/cancel
  swap: 'mdi:swap-horizontal',             // Swap/exchange
  refresh: 'mdi:refresh',                  // Refresh/reload
} as const

/**
 * Time Icons - Calendar and clock related
 */
export const TIME_ICONS = {
  calendar: 'mdi:calendar',                // Date/calendar
  clock: 'mdi:clock-outline',              // Time
  history: 'mdi:history',                  // History/changelog
  sun: 'mdi:white-balance-sunny',          // Light mode/day
  moon: 'mdi:moon-waning-crescent',        // Dark mode/night
  palette: 'mdi:palette',                  // Theme/colors
  image: 'mdi:image-outline',              // Background/image
} as const

/**
 * Navigation Icons - Arrows and directional indicators
 */
export const NAVIGATION_ICONS = {
  arrowLeft: 'mdi:arrow-left',             // Back/previous
  arrowRight: 'mdi:arrow-right',           // Forward/next
  arrowDown: 'mdi:arrow-down',             // Down
  arrowUp: 'mdi:arrow-up',                 // Up
  chevronDown: 'mdi:chevron-down',         // Dropdown
  chevronUp: 'mdi:chevron-up',             // Collapse
} as const

/**
 * Status Icons - Feedback and state indicators
 */
export const STATUS_ICONS = {
  check: 'mdi:check',                      // Success/checked
  checkCircle: 'mdi:check-circle-outline', // Success (outline) - subtle for tables
  tickCircle: 'mdi:check-circle-outline',  // Success (outline) - alias
  closeCircle: 'mdi:close-circle-outline', // Error/close (outline) - subtle for tables
  alertCircle: 'mdi:alert-circle-outline', // Warning
  info: 'mdi:information-outline',         // Information
  draw: 'mdi:handshake-outline',           // Draw result indicator
  lock: 'mdi:lock',                        // Locked/restricted
} as const

/**
 * Document Icons - Files and viewing
 */
export const DOCUMENT_ICONS = {
  clipboard: 'mdi:clipboard-text',         // Report/document
  eye: 'mdi:eye',                          // View/preview
} as const

/**
 * Unified icon collection - All icons in one place
 */
export const ICONS = {
  // Sports
  ...SPORT_ICONS,
  
  // Competition
  ...COMPETITION_ICONS,
  
  // Users
  ...USER_ICONS,
  
  // Organization
  ...ORGANIZATION_ICONS,
  
  // Actions
  ...ACTION_ICONS,
  
  // Time
  ...TIME_ICONS,
  
  // Navigation
  ...NAVIGATION_ICONS,
  
  // Status
  ...STATUS_ICONS,
  
  // Documents
  ...DOCUMENT_ICONS,
} as const

/**
 * Type for icon names - provides autocomplete and type safety
 */
export type IconName = typeof ICONS[keyof typeof ICONS]

/**
 * Helper function to get icon string by key
 * Useful for dynamic icon selection
 */
export function getIcon(name: keyof typeof ICONS): string {
  return ICONS[name]
}

/**
 * Type guard to check if a string is a valid icon name
 */
export function isValidIcon(icon: string): icon is IconName {
  return Object.values(ICONS).includes(icon as IconName)
}

// Export type for the Icon component props for convenience
export type IconProps = React.ComponentProps<typeof IconifyIcon>
